import { inject, Pipe, type PipeTransform } from '@angular/core';
import type { SeoParamsOf, SeoRouteName } from '@handyhand/i18n';
import { LocalizationService } from '@services/logic/localization.service';

export interface SeoRoutePipeOptions<N extends SeoRouteName> {
  params?: SeoParamsOf<N>;
  query?: Record<string, string | number | boolean>;
  absolute?: boolean;
}

/**
 * Resolves a named SEO route (static landing page) to a URL path. Thin
 * wrapper around `LocalizationService.seoRoute()` which owns the country
 * resolution and ensures routes always follow the hostname.
 */
@Pipe({ name: 'seoRoute', standalone: true, pure: true })
export class SeoRoutePipe implements PipeTransform {
  private readonly localizationService = inject(LocalizationService);

  transform<N extends SeoRouteName>(
    name: N,
    options?: SeoRoutePipeOptions<N>,
  ): string {
    const resolverOptions: {
      query?: Record<string, string | number | boolean>;
      absolute?: boolean;
    } = {};

    if (options?.query !== undefined) {
      resolverOptions.query = options.query;
    }

    if (options?.absolute !== undefined) {
      resolverOptions.absolute = options.absolute;
    }

    return this.localizationService.seoRoute(
      name,
      options?.params,
      resolverOptions,
    );
  }
}
